import React, { useRef, useState } from 'react';
import { Button, ConfirmButton } from '@ygtechnologies/rubus-ui';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { faEye, faIndent } from '@fortawesome/pro-light-svg-icons';
import { extractErrorMessage } from '@/utils/ajax';
import { useLocalStorage } from '@/utils/hooks';
import CodeEditor, { CodeEditorHandle } from '@/components/CodeEditor/CodeEditor';
import { EmailPreviewKind } from '@/services/emailTemplates';
import PlaceholderReference from './PlaceholderReference';
import TemplatePreview from './TemplatePreview';
import theme from './EmailTemplates.module.scss';

export interface TemplateEditorTarget {
    /** Identifies the scope being edited, so changing context re-seeds the editor. */
    key: string;
    kind: EmailPreviewKind;
    /** The global template is the source-controlled baseline and cannot be edited here. */
    readOnly: boolean;
    /** Content templates have a subject; layouts do not. */
    hasSubject: boolean;
    subject: string;
    template: string;
    languageCode: string | null;
    /** Project whose branding and layout the preview should use, when one is in context. */
    projectId: number | null;
    /** Seller profile whose layout the preview resolves when no project is chosen. */
    profileId: number | null;
    /** True when saving will create the override rather than update an existing one. */
    isNew: boolean;
    /** Where the shown content came from, e.g. "Seeded from the global template". */
    sourceNote: string;
}

interface TemplateEditorPanelProps {
    target: TemplateEditorTarget;
    /** Disables Save and Delete, e.g. while the resolution for a newly picked scope is still loading. */
    actionsDisabled?: boolean;
    /** Page-level controls rendered in front of the source note, e.g. reopening the overrides sidebar. */
    headerActions?: React.ReactNode;
    onSave(values: { subject: string; template: string }): Promise<void>;
    onDelete?(): Promise<void>;
}

/**
 * Editor for the template at the scope currently selected in the bar: the MJML + Handlebars source, the
 * subject for content templates, and a live preview. Saving writes straight to the live tables.
 */
const TemplateEditorPanel: React.FC<TemplateEditorPanelProps> = ({
    target,
    actionsDisabled,
    headerActions,
    onSave,
    onDelete,
}) => {
    const [subject, setSubject] = useState(target.subject);
    const [template, setTemplate] = useState(target.template);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isFormatting, setIsFormatting] = useState(false);
    const editorRef = useRef<CodeEditorHandle>(null);
    // Remembered across scopes and visits — hiding the preview gives the source the full width.
    const [showPreview, setShowPreview] = useLocalStorage('emailTemplateShowPreview', true);

    // Re-seed when the context changes (one panel instance serves every scope). Keyed on target.key
    // alone — a background refetch after a save must not clobber keystrokes typed while it ran.
    // Done during render (not in an effect) so the preview sees the new scope and its re-seeded
    // template in one commit instead of two.
    const [seededKey, setSeededKey] = useState(target.key);
    if (seededKey !== target.key) {
        setSeededKey(target.key);
        setSubject(target.subject);
        setTemplate(target.template);
    }

    const isDirty = subject !== target.subject || template !== target.template;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await onSave({ subject, template });
            Toast.success(target.isNew ? 'Override created' : 'Template saved');
        } catch (e) {
            Toast.error((await extractErrorMessage(e)) ?? 'Something went wrong');
        } finally {
            setIsSaving(false);
        }
    };

    // Only the button awaits this; the editor's own Shift+Alt+F runs the same thing without the spinner.
    const handleFormat = async () => {
        setIsFormatting(true);
        try {
            await editorRef.current?.format();
        } finally {
            setIsFormatting(false);
        }
    };

    const handleDelete = async () => {
        if (!onDelete) {
            return;
        }
        setIsDeleting(true);
        try {
            await onDelete();
            Toast.success('Override deleted');
        } catch (e) {
            Toast.error((await extractErrorMessage(e)) ?? 'Something went wrong');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className={theme.editor}>
            <div className={theme.editorHeader}>
                <div className={theme.editorHeaderLead}>
                    {headerActions}
                    <p className={theme.hint}>
                        <span className={theme.sourceNote}>{target.sourceNote}</span>
                        {' — '}
                        {target.readOnly
                            ? 'global templates cannot be edited here. Pick a project or seller profile above to override it.'
                            : 'saving takes effect immediately; the mail pipeline reads these rows directly.'}
                    </p>
                </div>
                <div className={theme.editorActions}>
                    {!target.readOnly && onDelete && (
                        <ConfirmButton
                            size="sm"
                            variant="danger"
                            dialogTitle="Delete override"
                            dialogBody="Emails will fall back to the next level of the resolution order. Continue?"
                            confirmButton={{ variant: 'danger' }}
                            loading={isDeleting}
                            disabled={actionsDisabled}
                            onConfirm={handleDelete}>
                            Delete override
                        </ConfirmButton>
                    )}
                    {!target.readOnly && (
                        <Button
                            size="sm"
                            variant="primary"
                            disabled={!isDirty || actionsDisabled}
                            loading={isSaving}
                            onClick={handleSave}>
                            {target.isNew ? 'Create override' : 'Save'}
                        </Button>
                    )}
                </div>
            </div>

            {target.hasSubject && (
                <label className={theme.subjectField}>
                    Subject
                    <input
                        type="text"
                        className="form-control"
                        value={subject}
                        readOnly={target.readOnly}
                        onChange={(e) => setSubject(e.target.value)}
                    />
                </label>
            )}

            <div className={showPreview ? theme.workspaceSplit : theme.workspace}>
                <div className={theme.pane}>
                    <div className={theme.templateField}>
                        <div className={theme.templateFieldHeader}>
                            Template (MJML + Handlebars)
                            <div className={theme.templateFieldActions}>
                                <PlaceholderReference
                                    kind={target.kind}
                                    onInsert={
                                        target.readOnly ? undefined : (text) => editorRef.current?.insertText(text)
                                    }
                                />
                                {!target.readOnly && (
                                    <Button
                                        size="xs"
                                        variant="tertiary"
                                        icon={faIndent}
                                        loading={isFormatting}
                                        title="Re-indent the template (Shift+Alt+F)"
                                        onClick={handleFormat}>
                                        Format
                                    </Button>
                                )}
                                {/* Hiding lives on the preview pane itself; this only offers the way back,
                                    sitting where the preview would otherwise begin. */}
                                {!showPreview && (
                                    <Button
                                        size="xs"
                                        variant="tertiary"
                                        icon={faEye}
                                        onClick={() => setShowPreview(true)}>
                                        Show preview
                                    </Button>
                                )}
                            </div>
                        </div>
                        <CodeEditor ref={editorRef} value={template} readOnly={target.readOnly} onChange={setTemplate} />
                    </div>

                    {target.kind === 'layout' ? (
                        <p className={theme.hint}>
                            The layout is a full MJML document wrapping every content template. Place the content with{' '}
                            <code>{'{{{body}}}'}</code> (triple braces — it is already HTML).
                        </p>
                    ) : (
                        <p className={theme.hint}>
                            Content is a fragment inserted into the layout, so it starts at{' '}
                            <code>&lt;mj-section&gt;</code> — no <code>&lt;mjml&gt;</code> or <code>&lt;mj-body&gt;</code>.
                        </p>
                    )}
                </div>

                {showPreview && (
                    <div className={theme.pane}>
                        <TemplatePreview
                            contextKey={target.key}
                            kind={target.kind}
                            template={template}
                            subject={target.hasSubject ? subject : undefined}
                            languageCode={target.languageCode}
                            projectId={target.projectId}
                            profileId={target.profileId}
                            onHide={() => setShowPreview(false)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemplateEditorPanel;
